// Making only the emoji bigger inside the compose box.
//
// The message list can wrap emoji in a span (see emojiText.js), but the
// compose box is a <textarea> and a textarea cannot style a substring - every
// character in it renders at one size. The one mechanism that can tell them
// apart is the font stack: an @font-face carrying a unicode-range is consulted
// only for the code points in that range, and size-adjust scales the face it
// resolves to. So an emoji-only face listed first draws emoji larger and
// leaves every letter beside them untouched.
//
// This degrades to exactly today's behaviour rather than breaking: if
// size-adjust is unsupported, or none of the local() names resolve, the
// browser falls through to the next family in the stack.

export const EMOJI_FONT_FAMILY = 'TeamXEmoji';

// The compose text is 14px, so this draws emoji at about 17px. Enough to stop
// them reading as punctuation, small enough that they still sit inside the
// 16px line the bar is built around - the field is capped at three of those
// lines and the bar cannot grow, so a much larger glyph would collide with the
// line above it.
export const EMOJI_SIZE_ADJUST = '120%';

// The emoji blocks, minus the code points that are ordinary punctuation far
// more often than they are emoji. (c), (r) and (tm) are deliberately absent:
// enlarging those mid-sentence would be a bug, not a feature.
export const EMOJI_UNICODE_RANGE = [
    'U+203C', 'U+2049', 'U+2139', 'U+2194-21AA', 'U+231A-231B', 'U+2328',
    'U+23CF', 'U+23E9-23FA', 'U+24C2', 'U+25AA-25FE', 'U+2600-27BF',
    'U+2934-2935', 'U+2B00-2BFF', 'U+3030', 'U+303D', 'U+3297', 'U+3299',
    'U+FE0F', 'U+20E3', 'U+1F000-1FAFF',
].join(', ');

export const STYLE_ELEMENT_ID = 'teamx-emoji-input-font';

export const fontFaceRule = () => '@font-face {'
    + ' font-family: ' + EMOJI_FONT_FAMILY + ';'
    + " src: local('Segoe UI Emoji'), local('Apple Color Emoji'), local('Noto Color Emoji');"
    + ' unicode-range: ' + EMOJI_UNICODE_RANGE + ';'
    + ' size-adjust: ' + EMOJI_SIZE_ADJUST + ';'
    + ' }';

// The stack to put on the input. The emoji face has to come first or the
// body font answers for the emoji code points before it is ever consulted;
// everything after it is MUI v4's own default, so ordinary text is unchanged.
export const composeFontFamily = "'" + EMOJI_FONT_FAMILY + "', 'Roboto', 'Helvetica', 'Arial', sans-serif";

// Installs the rule in one document, once. The caller passes the document the
// input is actually in - the chat renders in a detached window, so the
// `document` this module closes over is the opener's and styling it would do
// nothing.
//
// Never throws: every call site is a React lifecycle, and a throw escaping one
// takes the whole tree down with it.
export const ensureEmojiFontFace = (doc) => {
    try {
        if (!doc || !doc.head || typeof doc.getElementById !== 'function') {
            return false;
        }
        if (doc.getElementById(STYLE_ELEMENT_ID)) {
            return true;
        }
        const style = doc.createElement('style');
        style.id = STYLE_ELEMENT_ID;
        style.textContent = fontFaceRule();
        doc.head.appendChild(style);
        return true;
    } catch (err) {
        return false;
    }
};

export default { ensureEmojiFontFace, composeFontFamily, fontFaceRule };



// WEBPACK FOOTER //
// ./src/utils/emojiInputFont.js